import { Box, Button, Stack, Typography, Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCheckDiscountCodeMutation } from "../../store/cart/api";
import { cartActions } from "../../store/cart/slice";
import { uiActions } from "../../store/ui/slice";
import TextInput from "../UI/TextInput";

const DiscountCode = () => {
  const dispatch = useDispatch();
  const { registrations, totalPrice, totalDiscount, discountCode: savedDiscountCode, hasClemsonCoupon } = useSelector((state) => state.cart);
  const programIds = [...new Set(registrations.map((reg) => reg.programId))];

  const [discountCode, setDiscountCode] = useState("");
  const [discountCodeError, setDiscountCodeError] = useState("");
  const [discountCodeDisabled, setDiscountCodeDisabled] = useState(false);

  // Initialize with saved discount code if it exists
  useEffect(() => {
    if (savedDiscountCode) {
      setDiscountCode(savedDiscountCode);
      setDiscountCodeDisabled(true);
    }
  }, [savedDiscountCode]);

  // Check if discount is already applied
  useEffect(() => {
    if (totalDiscount > 0) {
      setDiscountCodeDisabled(true);
    }
  }, [totalDiscount]);

  // Validate discount amount against current total price whenever price changes
  useEffect(() => {
    if (totalDiscount > 0 && !hasClemsonCoupon && totalPrice <= totalDiscount) {
      setDiscountCodeError("Discount value exceeds total price");
      //auto-remove the discount if it exceeds the total
      dispatch(cartActions.applyCoupon({ code: "", value: 0 }));
      dispatch(uiActions.setAlertWarning("Discount was removed as it exceeded your total"));
      setDiscountCode("");
      setDiscountCodeDisabled(false);
    } else if (discountCodeError === "Discount value exceeds total price" && totalPrice > totalDiscount) {
      // Clear the error if it's been resolved by a price change
      setDiscountCodeError("");
    }
  }, [totalPrice, totalDiscount, hasClemsonCoupon]);

  const [
    checkDiscountCode,
    {
      data: checkDiscountCodeData,
      error: checkDiscountCodeError,
      isSuccess: isSuccessCheckDiscountCode,
      isError: isErrorCheckDiscountCode,
    },
  ] = useCheckDiscountCodeMutation();

  // SUCCESS - checkDiscountCode
  useEffect(() => {
    if (!isSuccessCheckDiscountCode) {
      return;
    }

    if (checkDiscountCodeData?.value > 0) {
      if (checkDiscountCodeData.is_clemson_coupon) {
        // Handle Clemson coupon (up to $200 off per session)
        setDiscountCodeDisabled(true);
        setDiscountCodeError("");
        dispatch(cartActions.applyCoupon(checkDiscountCodeData));
        dispatch(uiActions.setAlertSuccess(
            "Coupon applied! Up to $200 off per session. For sessions paid in full, the discount applies immediately. For deposit payments, the discount will apply to your remaining balance."
        ));
      } else if (totalPrice <= checkDiscountCodeData.value) {
        setDiscountCodeDisabled(false);
        setDiscountCodeError("Discount value exceeds total price");
        return;
      } else {
        // Regular coupon
        setDiscountCodeDisabled(true);
        setDiscountCodeError("");
        dispatch(cartActions.applyCoupon(checkDiscountCodeData));
        dispatch(uiActions.setAlertSuccess("Coupon applied!"));
      }
    } else {
      setDiscountCodeDisabled(false);
      setDiscountCodeError("Invalid code");
    }
  }, [isSuccessCheckDiscountCode, checkDiscountCodeData]);

  // ERROR - checkDiscountCode
  useEffect(() => {
    if (isErrorCheckDiscountCode) {
      setDiscountCodeError("Invalid code");
      setDiscountCodeDisabled(false);
    }
  }, [isErrorCheckDiscountCode, checkDiscountCodeError]);

  const handleDiscountCodeChange = (e) => {
    setDiscountCode(e.target.value);
    setDiscountCodeError("");
  };

  const handleVerifyCode = () => {
    if (discountCode.length === 0) {
      setDiscountCodeError("Please enter a coupon code");
      return;
    }
    setDiscountCodeError("");
    setDiscountCodeDisabled(true);
    checkDiscountCode({
      programIds,
      discountCode,
    });
  };

  const handleRemoveDiscount = () => {
    setDiscountCode("");
    setDiscountCodeDisabled(false);
    setDiscountCodeError("");
    dispatch(cartActions.applyCoupon({ code: "", value: 0, is_clemson_coupon: false }));
    dispatch(uiActions.setAlertInfo("Discount removed"));
  };

  return (
      <>
        <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
          <Box sx={{ width: "50%" }}>
            <TextInput
                type="text"
                name="discountCode"
                label="Coupon Code"
                value={discountCode}
                onChange={handleDiscountCodeChange}
                error={discountCodeError}
                disabled={discountCodeDisabled}
            />
          </Box>
          {!discountCodeDisabled ? (
              <Button
                  variant="outlined"
                  onClick={handleVerifyCode}
                  sx={{ width: "50%" }}
                  disabled={!discountCode}
              >
                Verify Code
              </Button>
          ) : (
              <Button
                  variant="outlined"
                  color="error"
                  onClick={handleRemoveDiscount}
                  sx={{ width: "50%" }}
              >
                Remove Coupon
              </Button>
          )}
        </Stack>

        {hasClemsonCoupon && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Stack direction="row" alignItems="center">
                <Typography variant="body2">
                  <strong>Discount Applied:</strong> Up to $200 off per session
                  <Tooltip title="This discount offers up to $200 off per session. This amount is reduced by any other discounts you're eligible for, such as returning camper discount ($50) or early-bird discount ($50). For sessions paid in full, the discount applies immediately. For deposit payments, the discount applies to your remaining balance." placement="bottom">
                    <InfoIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle', fontSize: '16px', color: 'gray' }} />
                  </Tooltip>
                </Typography>
              </Stack>
            </Box>
        )}
      </>
  );
};

export default DiscountCode;